import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Eye, FlaskConical, Pause, Play, Plus, Trash2, Trophy,
} from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ABTestsTab() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [results, setResults] = useState(null);
  const [viewingTestId, setViewingTestId] = useState(null);
  const [form, setForm] = useState({
    name: '', article_slug: '*',
    variants: [
      { variant_id: 'control', cta_text: 'Shop Now', cta_url: '', color: 'neon', placement: 'banner', icon: 'arrow', subtitle: 'Recommended', weight: 50 },
      { variant_id: 'variant_b', cta_text: 'Get 10% Off', cta_url: '', color: 'red', placement: 'banner', icon: 'cart', subtitle: 'Limited Time', weight: 50 },
    ]
  });

  const token = sessionStorage.getItem('admin_token');

  const fetchTests = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/ab-tests`, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) setTests(await resp.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const createTest = async () => {
    const resp = await fetch(`${API_URL}/api/admin/ab-tests`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (resp.ok) { setShowCreate(false); fetchTests(); }
  };

  const toggleStatus = async (test) => {
    const newStatus = test.status === 'active' ? 'paused' : 'active';
    await fetch(`${API_URL}/api/admin/ab-tests/${test.test_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchTests();
  };

  const deleteTest = async (testId) => {
    await fetch(`${API_URL}/api/admin/ab-tests/${testId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    fetchTests();
    if (viewingTestId === testId) { setResults(null); setViewingTestId(null); }
  };

  const viewResults = async (testId) => {
    setViewingTestId(testId);
    const resp = await fetch(`${API_URL}/api/admin/ab-tests/${testId}/results`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.ok) setResults(await resp.json());
  };

  const updateFormVariant = (i, key, val) => {
    const newVariants = [...form.variants];
    newVariants[i] = { ...newVariants[i], [key]: val };
    setForm({ ...form, variants: newVariants });
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">CTA A/B Tests</h3>
          <p className="text-xs text-gray-400">Test different CTA styles to optimize affiliate revenue</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 border border-teal-200"
          data-testid="create-ab-test-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New Test
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="ab-test-create-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Test Name</label>
              <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Banner CTA - Shop vs Discount" data-testid="ab-test-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Article Slug (* = all)</label>
              <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={form.article_slug} onChange={e => setForm({...form, article_slug: e.target.value})} placeholder="* for all articles" data-testid="ab-test-slug-input" />
            </div>
          </div>

          <p className="text-xs font-semibold text-gray-600 mt-2">Variants</p>
          {form.variants.map((v, i) => (
            <div key={v.cta_url} className="grid grid-cols-6 gap-2 items-end bg-gray-50 p-3 rounded-lg">
              <div>
                <label className="text-[10px] text-gray-400">ID</label>
                <input className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.variant_id} onChange={e => updateFormVariant(i, 'variant_id', e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] text-gray-400">CTA Text</label>
                <input className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.cta_text} onChange={e => updateFormVariant(i, 'cta_text', e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] text-gray-400">URL</label>
                <input className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.cta_url} onChange={e => updateFormVariant(i, 'cta_url', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Color</label>
                <select className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.color} onChange={e => updateFormVariant(i, 'color', e.target.value)}>
                  <option value="neon">Neon Yellow</option>
                  <option value="red">Red</option>
                  <option value="teal">Teal</option>
                  <option value="dark">Dark</option>
                  <option value="gradient">Gradient</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Placement</label>
                <select className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.placement} onChange={e => updateFormVariant(i, 'placement', e.target.value)}>
                  <option value="banner">Banner</option>
                  <option value="inline">Inline Button</option>
                  <option value="sidebar">Sidebar</option>
                  <option value="sticky">Sticky</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Weight</label>
                <input type="number" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded" value={v.weight} onChange={e => updateFormVariant(i, 'weight', +e.target.value)} min={1} max={100} />
              </div>
            </div>
          ))}

          <div className="flex gap-2">
            <button onClick={() => setForm({...form, variants: [...form.variants, { variant_id: `v${form.variants.length}`, cta_text: 'New CTA', cta_url: '', color: 'teal', placement: 'inline', icon: 'arrow', weight: 50 }]})} className="text-xs text-teal-600 hover:text-teal-800">+ Add Variant</button>
            {form.variants.length > 2 && <button onClick={() => setForm({...form, variants: form.variants.slice(0, -1)})} className="text-xs text-red-500 hover:text-red-700">- Remove Last</button>}
          </div>

          <button onClick={createTest} className="px-4 py-2 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800" data-testid="ab-test-submit-btn">Create Test</button>
        </div>
      )}

      {/* Tests List */}
      <div className="space-y-3">
        {tests.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No A/B tests yet. Create your first test to start optimizing CTAs.</p>}
        {tests.map(test => (
          <div key={test.test_id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`ab-test-${test.test_id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <FlaskConical className="w-4 h-4 text-purple-500" />
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{test.name}</h4>
                  <p className="text-[10px] text-gray-400">Slug: {test.article_slug} | {test.variants?.length} variants | Created {new Date(test.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${test.status === 'active' ? 'bg-green-50 text-green-600' : test.status === 'paused' ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-50 text-gray-500'}`}>
                  {test.status}
                </span>
                <button onClick={() => viewResults(test.test_id)} className="p-1.5 text-gray-400 hover:text-purple-600 transition-colors" title="View Results" data-testid={`view-results-${test.test_id}`}>
                  <BarChart3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggleStatus(test)} className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors" title={test.status === 'active' ? 'Pause' : 'Resume'} data-testid={`toggle-${test.test_id}`}>
                  {test.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => deleteTest(test.test_id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" title="Delete" data-testid={`delete-test-${test.test_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {/* Variant pills */}
            <div className="flex gap-2 mt-2">
              {test.variants?.map((v, i) => (
                <span key={v.cta_text} className="px-2 py-0.5 text-[10px] bg-gray-50 text-gray-600 rounded border border-gray-100">
                  {v.variant_id}: "{v.cta_text}" ({v.placement}, {v.color})
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Results Panel */}
      {results && viewingTestId && (
        <div className="bg-white rounded-xl border border-purple-200 p-5" data-testid="ab-test-results-panel">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-purple-500" /> Results: {results.test?.name}
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <p className="text-xl font-bold text-gray-900">{results.total_impressions?.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400">Total Impressions</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <p className="text-xl font-bold text-gray-900">{results.total_clicks?.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400">Total Clicks</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <p className="text-xl font-bold text-gray-900">{results.total_impressions > 0 ? ((results.total_clicks / results.total_impressions) * 100).toFixed(2) : 0}%</p>
              <p className="text-[10px] text-gray-400">Overall CTR</p>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                <th className="pb-2">Variant</th>
                <th className="pb-2 text-right">Impressions</th>
                <th className="pb-2 text-right">Clicks</th>
                <th className="pb-2 text-right">Conv. Rate</th>
                <th className="pb-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {results.results?.map((r, i) => (
                <tr key={r.label} className="border-b border-gray-50 last:border-0">
                  <td className="py-2.5 text-gray-700 text-xs font-medium">{r.label} <span className="text-gray-400">({r.variant_id})</span></td>
                  <td className="py-2.5 text-right font-mono text-gray-500">{r.impressions?.toLocaleString()}</td>
                  <td className="py-2.5 text-right font-mono text-gray-700 font-medium">{r.clicks?.toLocaleString()}</td>
                  <td className="py-2.5 text-right">
                    {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                    <span className={`px-2 py-0.5 text-xs rounded-full font-mono ${r.conversion_rate >= 5 ? 'bg-green-50 text-green-600' : r.conversion_rate > 0 ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-50 text-gray-400'}`}>
                      {r.conversion_rate}%
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    {results.winner?.variant_id === r.variant_id && (
                      <span className="flex items-center gap-1 justify-end text-xs text-amber-600">
                        <Trophy className="w-3 h-3" /> Winner
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {results.winner ? (
            <p className="text-xs text-green-600 mt-3 font-medium">Winner: "{results.winner.label}" with {results.winner.conversion_rate}% conversion rate</p>
          ) : (
            <p className="text-xs text-gray-400 mt-3">Need at least 10 impressions per variant to determine a winner</p>
          )}
        </div>
      )}

      {/* Stack Strategy A/B Tests */}
      <StackABTestsSection />
    </div>
  );
}

export function StackABTestsSection() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [results, setResults] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [form, setForm] = useState({
    name: '', variants: [
      { variant_id: 'conservative', label: 'Conservative (3 max)', max_core: 3, include_optional: false, weight: 50 },
      { variant_id: 'comprehensive', label: 'Comprehensive (5+)', max_core: 99, include_optional: true, weight: 50 },
    ]
  });
  const token = sessionStorage.getItem('admin_token');

  const fetchTests = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/stack-ab-tests`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setTests(await r.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const createTest = async () => {
    const r = await fetch(`${API_URL}/api/admin/stack-ab-tests`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (r.ok) { setShowCreate(false); fetchTests(); }
  };

  const toggleStatus = async (t) => {
    await fetch(`${API_URL}/api/admin/stack-ab-tests/${t.test_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: t.status === 'active' ? 'paused' : 'active' }),
    });
    fetchTests();
  };

  const deleteTest = async (id) => {
    await fetch(`${API_URL}/api/admin/stack-ab-tests/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchTests();
    if (viewingId === id) { setResults(null); setViewingId(null); }
  };

  const viewResults = async (id) => {
    setViewingId(id);
    const r = await fetch(`${API_URL}/api/admin/stack-ab-tests/${id}/results`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setResults(await r.json());
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-4 pt-6 border-t border-gray-200" data-testid="stack-ab-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-purple-500" /> Peptide Stack A/B Tests
          </h3>
          <p className="text-xs text-gray-400">Test recommendation strategies to optimize affiliate conversions</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 border border-purple-200"
          data-testid="create-stack-ab-btn">
          <Plus className="w-3.5 h-3.5" /> New Stack Test
        </button>
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl border border-purple-200 p-5 space-y-4" data-testid="stack-ab-create-form">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Test Name</label>
            <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Conservative vs Comprehensive" data-testid="stack-ab-name-input" />
          </div>
          <p className="text-xs font-semibold text-gray-600">Strategy Variants</p>
          {form.variants.map((v, i) => (
            <div key={v.label} className="grid grid-cols-5 gap-2 bg-gray-50 p-3 rounded-lg">
              <div>
                <label className="text-[10px] text-gray-400">Label</label>
                <input className="w-full px-2 py-1.5 text-xs border rounded" value={v.label} onChange={e => { const nv = [...form.variants]; nv[i] = { ...nv[i], label: e.target.value }; setForm({ ...form, variants: nv }); }} data-testid={`stack-ab-label-${i}`} />
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Max Core Peptides</label>
                <input type="number" min="1" max="13" className="w-full px-2 py-1.5 text-xs border rounded" value={v.max_core} onChange={e => { const nv = [...form.variants]; nv[i] = { ...nv[i], max_core: parseInt(e.target.value) || 3 }; setForm({ ...form, variants: nv }); }} data-testid={`stack-ab-maxcore-${i}`} />
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Include Optional</label>
                <select className="w-full px-2 py-1.5 text-xs border rounded" value={v.include_optional ? 'yes' : 'no'} onChange={e => { const nv = [...form.variants]; nv[i] = { ...nv[i], include_optional: e.target.value === 'yes' }; setForm({ ...form, variants: nv }); }}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400">Weight</label>
                <input type="number" min="1" max="100" className="w-full px-2 py-1.5 text-xs border rounded" value={v.weight} onChange={e => { const nv = [...form.variants]; nv[i] = { ...nv[i], weight: parseInt(e.target.value) || 50 }; setForm({ ...form, variants: nv }); }} />
              </div>
              <div className="flex items-end">
                {form.variants.length > 2 && (
                  <button onClick={() => setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-600 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={() => setForm({ ...form, variants: [...form.variants, { variant_id: `v${form.variants.length}`, label: `Strategy ${form.variants.length + 1}`, max_core: 4, include_optional: true, weight: 50 }] })}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium">+ Add Variant</button>
            <div className="flex-1" />
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg">Cancel</button>
            <button onClick={createTest} className="px-4 py-2 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="stack-ab-create-submit">Create Test</button>
          </div>
        </div>
      )}

      {/* Tests List */}
      {tests.length === 0 && !showCreate ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FlaskConical className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-400">No stack A/B tests yet. Create one to start optimizing.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tests.map(t => (
            <div key={t.test_id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`stack-ab-test-${t.test_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{t.name}</h4>
                  <p className="text-[10px] text-gray-400">{t.variants?.length || 0} variants &middot; {t.status}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => viewResults(t.test_id)} className="text-xs text-purple-600 hover:text-purple-800 font-medium" data-testid={`stack-ab-results-${t.test_id}`}><Eye className="w-3.5 h-3.5 inline mr-1" />Results</button>
                  <button onClick={() => toggleStatus(t)} className={`p-1.5 rounded-lg ${t.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    {t.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => deleteTest(t.test_id)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                {t.variants?.map(v => (
                  <span key={v.variant_id} className="text-[10px] px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                    {v.label} ({v.max_core} max{v.include_optional ? ' +opt' : ''})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="bg-white rounded-xl border-2 border-purple-100 p-5" data-testid="stack-ab-results-panel">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">{results.test?.name} — Results</h3>
          <p className="text-xs text-gray-400 mb-3">
            {results.total_impressions} impressions &middot; {results.total_clicks} clicks &middot; {results.overall_ctr}% overall CTR
            {results.significant && <span className="ml-2 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] rounded-full font-semibold">{results.confidence} Significant</span>}
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-gray-400 font-medium">Strategy</th>
                <th className="text-center py-2 text-gray-400 font-medium">Config</th>
                <th className="text-center py-2 text-gray-400 font-medium">Views</th>
                <th className="text-center py-2 text-gray-400 font-medium">Clicks</th>
                <th className="text-center py-2 text-gray-400 font-medium">CTR</th>
                <th className="text-left py-2 text-gray-400 font-medium">Top Peptides</th>
              </tr>
            </thead>
            <tbody>
              {results.results?.map((r, i) => (
                <tr key={r.variant_id} className={`border-b border-gray-50 ${i === 0 && results.significant ? 'bg-green-50/50' : ''}`}>
                  <td className="py-2 text-gray-900 font-medium">{r.label} {i === 0 && results.winner && <span className="text-green-600">*</span>}</td>
                  <td className="py-2 text-center text-gray-500">{r.max_core} core{r.include_optional ? ' +opt' : ''}</td>
                  <td className="py-2 text-center font-mono">{r.impressions}</td>
                  <td className="py-2 text-center font-mono">{r.clicks}</td>
                  <td className="py-2 text-center font-mono font-bold">{r.ctr}%</td>
                  <td className="py-2">
                    {r.top_peptides?.map(p => (
                      <span key={p.peptide} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded mr-1">{p.peptide} ({p.clicks})</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {results.winner ? (
            <p className="text-xs text-green-600 mt-3 font-medium">Winner: "{results.winner.label}" at {results.winner.ctr}% CTR ({results.confidence} confidence)</p>
          ) : (
            <p className="text-xs text-gray-400 mt-3">Need more data for statistical significance (min 30 impressions total, 10 per variant)</p>
          )}
        </div>
      )}
    </div>
  );
}
